import React, { useContext, useEffect, useState } from "react";
import { CButton, CCard, CCardHeader, CCol, CRow, CBadge, CSpinner, CTooltip } from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilPlus, cilFilter, cilReload, cilX } from "@coreui/icons";
import DownloadExcel from "src/reusables/DownloadExcel";
import { useLocation, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import formatter from "src/reusables/formatter";
import { getAll } from "src/services/Stores";
import { Toaster } from "src/components/Toaster";
import { AuthContext } from "src/App";
import StoreFilter from "./Filters"; // You'll need to create this component
import { usePaginationState } from "src/reusables/usePaginationState";

function Stores() {
  const [dataRows, setDataRows] = useState([]);
  const [dataColumns, setDataColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [color, setColor] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const navigate = useNavigate();
  const Auth = useContext(AuthContext);
  const location = useLocation();
  const excelName = "Daftar Toko per " + formatter("dateOnly", new Date());

  async function fetchMainData() {
    try {
      setLoading(true);
      const res = await getAll(filters);

      const rows = res.stores?.map((item, i) => ({
        no: i + 1,
        id: item.id,
        name: item.name,
        address: item.address,
        last_visited: item.last_visited ? formatter("completeDate", item.last_visited) : "-",
        sales_id: item.sales_id,
        type: item.type,
        days_since_last_visit: item.days_since_last_visit ? `${item.days_since_last_visit} hari` : "-",
        smd: item.smdName,
        status: item.status || "active",
      }));

      setDataRows(rows || []);
    } catch (error) {
      setMessage("Gagal memuat data toko");
      setColor("danger");
      setOpen(true);
      console.error("Error fetching stores:", error);
    } finally {
      setLoading(false);
    }
  }
  const [paginationModel, setPaginationModel] = usePaginationState();
  useEffect(() => {
    fetchMainData();
  }, [filters]);
  useEffect(() => {
    if (location.state?.page !== undefined) {
      setPaginationModel({
        page: location.state.page,
        pageSize: location.state.pageSize || paginationModel.pageSize,
      });
    }
  }, [location.state]);
  const columns = [
    {
      field: "no",
      headerName: "No",
      width: 60,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "name",
      headerName: "Nama Toko",
      flex: 1,
      renderCell: (params) => <div style={{ fontWeight: 500 }}>{params.value}</div>,
    },
    {
      field: "type",
      headerName: "Tipe",
      width: 100,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "smd",
      headerName: "SMD",
      width: 100,
    },
    {
      field: "last_visited",
      headerName: "Kunjungan Terakhir",
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "days_since_last_visit",
      headerName: "Hari Sejak Kunjungan",
      width: 150,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <CBadge
          color={
            params.value === "-"
              ? "secondary"
              : parseInt(params.value) > 30
              ? "danger"
              : parseInt(params.value) > 14
              ? "warning"
              : "success"
          }
        >
          {params.value}
        </CBadge>
      ),
    },
  ];

  function handleRowClick(params) {
    navigate(`/stores/details/${params.row.id}`, {
      state: {
        fromPage: paginationModel.page,
        fromPageSize: paginationModel.pageSize,
      },
    });
  }

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setFilterOpen(false);
  };
  // Helper functions for filter display
  const getFilterLabel = (key) => {
    const labels = {
      name: "Nama",
      type: "Tipe",
      smd: "SMD",
      status: "Status",
      daysSinceVisit: "Hari Kunjungan",
    };
    return labels[key] || key;
  };

  const getFilterValueDisplay = (key, value) => {
    if (key === "daysSinceVisit") {
      const ranges = {
        7: "< 7 hari",
        14: "7-14 hari",
        30: "15-30 hari",
        31: "> 30 hari",
      };
      return ranges[value] || value;
    }
    return value;
  };

  // Filter handlers
  const handleRemoveFilter = (key) => {
    setFilters((prev) => ({ ...prev, [key]: "" }));
  };

  const handleResetFilters = () => {
    setFilters({
      name: "",
      type: "",
      smd: "",
      status: "",
      daysSinceVisit: "",
    });
  };
  return (
    <div style={{ color: "black" }}>
      <Toaster open={open} message={message} setOpen={setOpen} color={color} />

      <CCard>
        <CCardHeader className="p-3 p-md-4">
          <div className="d-flex flex-column gap-3">
            <CRow className="align-items-center g-3">
              <CCol xs={12} md={6} className="pe-md-3">
                <h4 className="mb-0">Daftar Toko</h4>
              </CCol>

              <CCol xs={12} md={6} className="ps-md-3">
                <div className="d-flex flex-wrap justify-content-md-end gap-2">
                  <CTooltip content="Refresh Data">
                    <CButton color="light" onClick={fetchMainData} disabled={loading} className="p-2" shape="pill">
                      <CIcon icon={cilReload} />
                    </CButton>
                  </CTooltip>

                  <CTooltip content="Filter Data">
                    <CButton color="light" onClick={() => setFilterOpen(true)} className="p-2" shape="pill">
                      <CIcon icon={cilFilter} />
                    </CButton>
                  </CTooltip>

                  <CButton color="primary" onClick={() => navigate("/store-actions")} className="px-3">
                    <CIcon icon={cilPlus} className="me-2" />
                    Tambah Toko
                  </CButton>

                  <DownloadExcel
                    rows={dataRows}
                    columns={columns.filter((col) => col.field !== "actions")}
                    excelName={excelName}
                    className="ms-2"
                  />
                </div>
              </CCol>
            </CRow>

            {/* Filter Chips Section */}
            {Object.keys(filters).some((key) => filters[key]) && (
              <div className="d-flex flex-wrap align-items-center gap-2">
                <small className="text-muted me-2">Filter Aktif:</small>
                {Object.entries(filters).map(
                  ([key, value]) =>
                    value && (
                      <CBadge
                        key={key}
                        color="primary"
                        className="d-flex align-items-center py-2 px-3"
                        shape="rounded-pill"
                      >
                        <span className="me-2">
                          {getFilterLabel(key)}: {getFilterValueDisplay(key, value)}
                        </span>
                        <div
                          role="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFilter(key);
                          }}
                          className="d-flex align-items-center"
                        >
                          <CIcon icon={cilX} width={14} />
                        </div>
                      </CBadge>
                    )
                )}
                <CButton color="link" size="sm" className="text-decoration-none p-0 ms-2" onClick={handleResetFilters}>
                  Clear All
                </CButton>
              </div>
            )}
          </div>
        </CCardHeader>

        <div style={{ margin: "16px", minHeight: "400px" }}>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
              <CSpinner color="primary" />
            </div>
          ) : (
            <DataGrid
              rows={dataRows}
              columns={columns}
              onRowClick={handleRowClick}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 20, 25]}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-columnHeader:focus": {
                  outline: "none",
                },
              }}
            />
          )}
        </div>
      </CCard>

      {/* Filter Modal */}
      <StoreFilter
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={handleApplyFilters}
        initialFilters={filters}
      />
    </div>
  );
}

export default Stores;
